import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { sharingApi } from '../../lib/api/sharingApi';
import { LinkSharing } from './LinkSharing';
import { ShareList } from './ShareList';
import { UserSearch } from './UserSearch';
export function ShareDialog({ chatId, chatTitle, isOpen, onClose }) {
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const loadSettings = useCallback(async ()=>{
        setIsLoading(true);
        setError(null);
        try {
            const data = await sharingApi.getShareSettings(chatId);
            setSettings(data);
        } catch (_err) {
            setError('Failed to load sharing settings');
        } finally{
            setIsLoading(false);
        }
    }, [
        chatId
    ]);
    useEffect(()=>{
        if (isOpen) {
            loadSettings();
        }
    }, [
        isOpen,
        loadSettings
    ]);
    const handleAddUsers = async (users)=>{
        try {
            await sharingApi.shareChat(chatId, {
                users
            });
            await loadSettings();
        } catch (_err) {
            setError('Failed to share chat');
        }
    };
    const handleRemoveShare = async (shareId)=>{
        try {
            await sharingApi.revokeShare(chatId, shareId);
            await loadSettings();
        } catch (_err) {
            setError('Failed to remove share');
        }
    };
    const handleToggleLink = async (enabled)=>{
        try {
            await sharingApi.toggleLink(chatId, enabled);
            await loadSettings();
        } catch (_err) {
            setError('Failed to update link');
        }
    };
    const handleRegenerateLink = async ()=>{
        try {
            await sharingApi.regenerateLink(chatId);
            await loadSettings();
        } catch (_err) {
            setError('Failed to regenerate link');
        }
    };
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50",
        "data-testid": Testids.sharing.dialog,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "bg-background border border-border rounded-lg shadow-lg w-full max-w-md mx-4",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between p-4 border-b border-border",
                    children: [
                        /*#__PURE__*/ _jsxs("h2", {
                            className: "text-lg font-semibold",
                            children: [
                                'Share "',
                                chatTitle,
                                '"'
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            onClick: onClose,
                            className: "p-1 hover:bg-muted rounded",
                            "aria-label": "Close",
                            children: /*#__PURE__*/ _jsx(X, {
                                className: "w-5 h-5"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "p-4 max-h-96 overflow-y-auto",
                    children: [
                        error && /*#__PURE__*/ _jsx("div", {
                            className: "mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm",
                            children: error
                        }),
                        isLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "text-center text-muted-foreground text-sm",
                            children: "Loading..."
                        }) : settings ? /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-6",
                            children: [
                                /*#__PURE__*/ _jsxs("section", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-semibold mb-3",
                                            children: "Add people"
                                        }),
                                        /*#__PURE__*/ _jsx(UserSearch, {
                                            onSelect: handleAddUsers,
                                            existingUserIds: settings.directShares.map((s)=>s.userId),
                                            maxShares: settings.limits.maxDirectShares,
                                            currentCount: settings.limits.currentDirectShares,
                                            isLoading: isLoading
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("section", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-semibold mb-3",
                                            children: "People with access"
                                        }),
                                        /*#__PURE__*/ _jsx(ShareList, {
                                            shares: settings.directShares,
                                            onRemove: handleRemoveShare,
                                            isLoading: isLoading
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("section", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-semibold mb-3",
                                            children: "Link sharing"
                                        }),
                                        /*#__PURE__*/ _jsx(LinkSharing, {
                                            linkShare: settings.linkShare,
                                            onToggle: handleToggleLink,
                                            onRegenerate: handleRegenerateLink,
                                            isLoading: isLoading
                                        })
                                    ]
                                })
                            ]
                        }) : null
                    ]
                })
            ]
        })
    });
}
